import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import {
  BarChartIcon,
  ChatElipsisIcon,
  CheckmarkCircleIcon,
  CheckmarkIcon,
  ExclamationmarkTriangleIcon,
  PaperplaneIcon,
  PersonGroupIcon,
  TasklistIcon,
  UploadIcon,
} from '@navikt/aksel-icons';
import { PROCESS_STEPS, ProcessStep, StepState, dayAndMonth, stepState } from '../surveyProcessView';
import classes from '../WorkEnvironmentSurveyProcess.module.css';

const STEP_ICONS: Record<ProcessStep, typeof CheckmarkIcon> = {
  draft: CheckmarkIcon,
  sent: PaperplaneIcon,
  risk: ExclamationmarkTriangleIcon,
  data: UploadIcon,
  propose: ChatElipsisIcon,
  highlight: PersonGroupIcon,
  plan: TasklistIcon,
  followUp: CheckmarkCircleIcon,
  evaluation: BarChartIcon,
};

const TAG_COLORS: Record<StepState, 'success' | 'info' | 'neutral'> = {
  done: 'success',
  current: 'info',
  notStarted: 'neutral',
};

type Props = {
  current: number;
  createdAt: string;
  responseDeadline: string | null;
  /** Replaces the text of the risk step once the survey has closed */
  riskStepText?: string | null;
  /** Every step is done, as when the work is archived */
  finished?: boolean;
  /** What is left of the current step, when it is more than the step itself */
  currentStep?: { label: string; text: string } | null;
};

/** The nine steps, and what the current one is about. The steps only show where the work is. */
export const ProcessCard = ({
  current,
  createdAt,
  responseDeadline,
  riskStepText,
  finished = false,
  currentStep = null,
}: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const stateOf = (index: number): StepState => (finished ? 'done' : stepState(index, current));
  const override = (index: number) => (index === current && !finished ? currentStep : null);
  const stepText = (step: ProcessStep) =>
    step === 'risk' && riskStepText
      ? riskStepText
      : t(`workEnvironmentProcess.steps.${step}.text`, {
          date: dayAndMonth(step === 'draft' ? createdAt : (responseDeadline ?? createdAt), i18n.language),
        });
  const currentStepName = PROCESS_STEPS[current];
  const currentState = stateOf(current);

  return (
    <section aria-labelledby="process-heading" className={classes.card}>
      <div className={classes.processHeading}>
        <Heading level={2} data-size="xs" id="process-heading">
          {t('workEnvironmentProcess.process')}
        </Heading>
        <Paragraph data-size="sm" className={classes.subtle}>
          {t('workEnvironmentProcess.stepOf', { current: current + 1, total: PROCESS_STEPS.length })}
        </Paragraph>
      </div>
      <div className={classes.stepsScroll}>
        <ol className={classes.steps}>
          {PROCESS_STEPS.map((step, index) => {
            const state = stateOf(index);
            const Icon = state === 'done' ? CheckmarkIcon : STEP_ICONS[step];
            const title = t(`workEnvironmentProcess.steps.${step}.title`);
            return (
              <li
                key={step}
                className={classes.step}
                aria-current={index === current && !finished ? 'step' : undefined}
              >
                {index < PROCESS_STEPS.length - 1 && (
                  <span aria-hidden="true" className={classes.stepLine} data-state={state} />
                )}
                <span className={classes.stepItem}>
                  <span className={classes.stepDot} data-state={state} aria-hidden="true">
                    <Icon aria-hidden />
                  </span>
                  <span
                    className={`ds-label ${classes.stepTitle}`}
                    data-size="sm"
                    data-state={state}
                    aria-hidden="true"
                  >
                    {title}
                  </span>
                  <span className="ds-sr-only">
                    {t('workEnvironmentProcess.stepAria', {
                      number: index + 1,
                      title,
                      state: t(`workEnvironmentProcess.states.${state}`).toLowerCase(),
                    })}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      </div>
      <div className={classes.selectedStep}>
        <Tag data-size="sm" data-color={TAG_COLORS[currentState]}>
          {override(current)?.label ?? t(`workEnvironmentProcess.states.${currentState}`)}
        </Tag>
        <span className="ds-label">{t(`workEnvironmentProcess.steps.${currentStepName}.title`)}</span>
        <Paragraph data-size="sm" className={classes.subtle}>
          {override(current)?.text ?? stepText(currentStepName)}
        </Paragraph>
      </div>
    </section>
  );
};
